// ─── TemplateRenderer ─────────────────────────────────────────
// The core engine. Combines template config + invitation data
// + theme tokens into the final invitation page.
// Used for BOTH preview and published pages.

import React, { useRef, useContext, useMemo, useState, useEffect } from 'react';
import { resolveTheme } from '../../lib/tokens/themes';
import SectionRenderer from './SectionRenderer';
import { ErrorBoundary } from './ErrorBoundary';
import { CollageEditContext } from '../collage/CollageEditContext';

// Detect mobile viewport (matches the section-v1 reference width breakpoint).
// Used to disable position:fixed backgrounds — they cause severe scroll jank
// on iOS Safari (equivalent of `background-attachment: fixed`).
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

// ── Public Desktop Shell ───────────────────────────────────────
// Sitoa invitations are designed mobile-first (375-430px coordinate space).
// On guest-facing routes, we wrap the renderer in a centered 430px column so
// desktop browsers don't stretch the design across 1920px viewports. The
// outer wrapper carries the palette background so the shell looks intentional,
// not isolated. Studio editor + iframe preview (where `isEditor` is true via
// CollageEditContext) bypass this entirely — the editor canvas already has
// its own dimensioning logic and must stay unconstrained.
//
// Fixed-positioned descendants (e.g. `globalBg` with `bgScroll: 'fixed'`,
// gallery lightbox modals at `fixed inset-0`) escape this wrapper naturally
// and continue to fill the viewport — exactly the behaviour we want for
// full-bleed wallpapers and overlay sheets.
function PublicShell({ active, bgColor, children }) {
  if (!active) return children;
  return (
    <div
      className="min-h-screen w-full flex justify-center"
      style={{ backgroundColor: bgColor }}
      data-testid="public-page-background"
    >
      <div
        className="w-full"
        style={{ maxWidth: '430px' }}
        data-testid="public-invitation-shell"
      >
        {children}
      </div>
    </div>
  );
}

/**
 * @param {object} templateConfig - Full template config (sections, theme, settings)
 * @param {object} invitationData - Per-section content from invitation instance
 * @param {object} themeOverrides - Optional palette/font overrides
 * @param {object} settingsOverrides - Optional setting overrides
 * @param {string} slug - Invitation slug for RSVP form
 */
export default function TemplateRenderer({
  templateConfig,
  invitationData = {},
  themeOverrides = {},
  settingsOverrides = {},
  slug = '',
  layoutVersion,
}) {
  const rsvpRef = useRef(null);
  const editCtx = useContext(CollageEditContext);
  const isMobile = useIsMobile();
  // On mobile (and inside the editor), position:fixed backgrounds cause
  // severe scroll jank on iOS Safari. Force `scroll` mode on mobile so the
  // background scrolls naturally with the page. Desktop behavior unchanged.
  const isEditor = !!editCtx;

  // Resolve theme: template defaults + invitation overrides
  const theme = resolveTheme(templateConfig.theme, themeOverrides);

  // Merge settings
  const settings = { ...templateConfig.settings, ...settingsOverrides, slug };

  // Resolve sections: merge dynamicSections from settings with template defaults
  const resolvedSections = useMemo(() => {
    const dynamic = settingsOverrides.dynamicSections;
    if (dynamic && dynamic.length > 0) {
      const tmplById = {};
      (templateConfig.sections || []).forEach(s => { tmplById[s.id] = s; });
      let merged = dynamic.map(saved => ({
        ...(tmplById[saved.id] || {}),
        ...saved,
        elements: tmplById[saved.id]?.elements || saved.elements || [],
      }));
      // Apply sectionOrder and sectionVariants
      const savedOrder = settingsOverrides.sectionOrder || [];
      const savedVariants = settingsOverrides.sectionVariants || {};
      const savedVisibility = settingsOverrides.sectionVisibility || {};
      if (savedOrder.length > 0) {
        const byId = {};
        merged.forEach(s => { byId[s.id] = s; });
        const ordered = savedOrder.map(id => byId[id]).filter(Boolean);
        merged.forEach(s => { if (!savedOrder.includes(s.id)) ordered.push(s); });
        merged = ordered;
      }
      merged = merged.map(s => ({
        ...s,
        visible: savedVisibility[s.id] !== undefined ? savedVisibility[s.id] : (s.visible !== false),
        variant: savedVariants[s.id] || s.variant,
      }));
      return merged;
    }
    // Legacy: use template sections with visibility/variant overrides
    const savedVisibility = settingsOverrides.sectionVisibility || {};
    const savedVariants = settingsOverrides.sectionVariants || {};
    return (templateConfig.sections || []).map(s => ({
      ...s,
      visible: savedVisibility[s.id] !== undefined ? savedVisibility[s.id] : (s.visible !== false),
      variant: savedVariants[s.id] || s.variant,
    }));
  }, [templateConfig.sections, settingsOverrides]);

  // Global background (paper texture) from theme overrides
  const globalBg = themeOverrides.globalBg;
  // 'fixed' (default) anchors background to phone viewport (parallax look).
  // 'scroll' makes the background scroll with the page (poster look).
  // Phase 1.6 — Force `scroll` on mobile to avoid iOS background-attachment
  // jank. Desktop respects the organizer's setting.
  const rawBgScroll = themeOverrides.bgScroll || 'fixed';
  const bgScroll = (isMobile && !isEditor) ? 'scroll' : rawBgScroll;
  // For scroll mode: 'cover' fills the screen (crops sides), 'contain' shows full image
  const bgFit = themeOverrides.bgFit || 'cover';

  // Collect inline widgets — widget sections with canvasYPosition set (Studio mode)
  const inlineWidgets = (resolvedSections || [])
    .filter(s => s.visible && s.canvasYPosition != null && s.type !== 'collage')
    .map(s => ({
      section: s,
      data: { ...(s.data || {}), ...(invitationData[s.id] || {}) },
      yPosition: s.canvasYPosition,
    }))
    .sort((a, b) => a.yPosition - b.yPosition);

  const inlineWidgetIds = new Set(inlineWidgets.map(w => w.section.id));

  return (
    <PublicShell active={!isEditor} bgColor={theme.bg}>
      <main
        className="min-h-screen w-full relative"
        style={{
          backgroundColor: globalBg ? 'transparent' : theme.bg,
          color: theme.textPrimary,
          fontFamily: theme.bodyFont,
        }}
        data-testid="template-renderer"
        role="main"
        lang="en"
        onClick={(e) => {
          // Deselect when clicking the main background (between sections)
          if (e.target === e.currentTarget && editCtx?.onSelectElement) {
            editCtx.onSelectElement(null, null);
          }
        }}
      >
      {/* Global background — combinations:
          - Fixed + cover:   pinned to viewport, fills screen, may crop sides or top/bottom
          - Fixed + contain: pinned to viewport, full image at natural aspect (no crop)
          - Scroll + cover:  scrolls with the page, fills the FULL CANVAS, may crop sides
          - Scroll + contain: scrolls with the page, full image at natural aspect
          For `scroll` mode the layer must span the entire scrollable canvas
          (not 100vh) — otherwise long scroll backgrounds get cut at the first
          viewport height on tall mobile-first invitations. */}
      {globalBg && bgFit === 'contain' ? (
        // "Full image" — real <img> so natural aspect drives the size.
        // In scroll mode we still want it to fill the full canvas height —
        // `min-h-full` makes the wrapper reach the bottom of the parent (the
        // canvas section), while `object-cover` keeps the image's natural
        // aspect ratio centered.
        bgScroll === 'fixed' ? (
          <img
            src={globalBg}
            alt=""
            className="fixed left-0 right-0 top-0 w-full h-auto pointer-events-none select-none"
            style={{ zIndex: 0, display: 'block' }}
            loading="eager"
            decoding="async"
            fetchpriority="high"
            data-testid="global-bg-texture"
          />
        ) : (
          <img
            src={globalBg}
            alt=""
            className="absolute inset-0 w-full h-full pointer-events-none select-none"
            style={{ zIndex: 0, display: 'block', objectFit: 'contain', objectPosition: 'top center' }}
            loading="eager"
            decoding="async"
            fetchpriority="high"
            data-testid="global-bg-texture"
          />
        )
      ) : globalBg && (
        <div
          className={`${bgScroll === 'scroll' ? 'absolute inset-0' : 'fixed left-0 right-0 top-0'} pointer-events-none`}
          style={{
            // Fixed mode = pinned to viewport (100vh).
            // Scroll mode = span the full canvas via inset-0 so the bg moves
            // with the page across the entire scroll length, not just the
            // first 100vh.
            ...(bgScroll === 'scroll' ? null : { height: '100vh' }),
            backgroundImage: `url(${globalBg.startsWith('/') ? '' : ''}${globalBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
          }}
          data-testid="global-bg-texture"
        />
      )}

      {resolvedSections.map((section) => {
        if (!section.visible) return null;
        // Skip inline widgets — they render inside the collage canvas
        if (inlineWidgetIds.has(section.id)) return null;

        // Merge: template default data + invitation content for this section
        const sectionData = {
          ...(section.data || {}),
          ...(invitationData[section.id] || {}),
        };

        return (
          <ErrorBoundary key={section.id}>
            <div style={{ position: 'relative', zIndex: 1 }} data-section-type={section.type} id={`${section.type}-${section.id}`}>
              <SectionRenderer
                section={section}
                data={sectionData}
                theme={theme}
                settings={settings}
                rsvpRef={rsvpRef}
                globalBgActive={!!globalBg}
                inlineWidgets={section.type === 'collage' ? inlineWidgets : undefined}
                layoutVersion={layoutVersion}
                isEditing={isEditor}
              />
            </div>
          </ErrorBoundary>
        );
      })}
      </main>
    </PublicShell>
  );
}
